import React from "react";
import { Dialog, VisuallyHidden } from "radix-ui";
import { useBoundedContainer } from "@/hooks/useBoundedContainer";
import { cn } from "@/utils/cn";

export type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Not rendered visually — Radix requires a Dialog.Title for a11y. */
  title: string;
  side?: "left" | "right" | "bottom";
  width?: number | string;
  className?: string;
  /** Override the overlay's dim color — e.g. "bg-transparent" for a full-bleed
   * panel that should read as a page rather than a dialog floating over one. */
  overlayClassName?: string;
  /** DOM id of a `position: relative` element to portal into and bound to,
   * instead of the default full-viewport `document.body` + `fixed`. */
  boundedContainerId?: string;
  /**
   * Optional inline trigger, rendered via Radix's real `Dialog.Trigger` (asChild)
   * at the exact DOM position this component renders — e.g. inline in a toolbar,
   * not inside the sliding panel/portal. Gives correct click-to-toggle plus
   * `aria-haspopup`/`aria-expanded` for free. Omit and manage `open` externally
   * (e.g. a separate button calling `onOpenChange`) if the trigger needs to live
   * somewhere Sheet doesn't render, such as composed by a caller-owned wrapper.
   */
  trigger?: React.ReactElement;
  children: React.ReactNode;
};

const SLIDE_ANIMATION = {
  left: "data-[state=open]:animate-sheet-slide-in-left data-[state=closed]:animate-sheet-slide-out-left",
  right:
    "data-[state=open]:animate-sheet-slide-in-right data-[state=closed]:animate-sheet-slide-out-right",
  bottom:
    "data-[state=open]:animate-sheet-slide-in-bottom data-[state=closed]:animate-sheet-slide-out-bottom",
} as const;

const SIDE_POSITION = {
  left: "inset-y-0 left-0",
  right: "inset-y-0 right-0",
  bottom: "inset-x-0 bottom-0",
} as const;

function Sheet({
  open,
  onOpenChange,
  title,
  side = "left",
  width = 280,
  className = "",
  overlayClassName = "bg-black/40",
  boundedContainerId,
  trigger,
  children,
}: SheetProps) {
  // Single source of truth for the portal target AND the position class —
  // keeps them from disagreeing before the container resolves.
  const resolvedContainer = useBoundedContainer(boundedContainerId);
  const positionClass = resolvedContainer ? "absolute" : "fixed";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal container={resolvedContainer ?? undefined}>
        <Dialog.Overlay
          className={cn(
            positionClass,
            "inset-0 z-radixOverlay data-[state=open]:animate-sheet-fade-in data-[state=closed]:animate-sheet-fade-out",
            overlayClassName
          )}
        />
        <Dialog.Content
          style={side === "bottom" ? undefined : { width }}
          className={cn(
            positionClass,
            "z-radixContent flex flex-col overflow-y-auto overflow-x-hidden outline-none",
            SIDE_POSITION[side],
            SLIDE_ANIMATION[side],
            className
          )}
        >
          <VisuallyHidden.Root>
            <Dialog.Title>{title}</Dialog.Title>
          </VisuallyHidden.Root>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default Sheet;
